<template lang="pug">
.entity-selector(:ref="`entitySelect-${this._uid}`"
    :class="{'has-error': errorMessages.length, 'entity-selector-small': small, 'entity-selector-overflow' : !innerHeight, 'entity-select-dialog': appendOuterIcon}")
    v-select.test-0(
        v-if="filter && !autocomplete && type === 'select'"
        v-model="selectedValue"
        :label="labelText"
        :disabled="selectIsDisabled || disabled"
        :items="selectItems"
        item-text="label"
        :item-value="itemValue"
        :append-icon="appendIcon"
        :append-outer-icon="appendOuterIcon"
        :name="name"
        :height="innerHeight"
        :error-messages="errorMessages"
        :multiple="multiSelect"
        :chips="multiSelect"
        :deletable-chips="multiSelect"
        :hide-details="hideDetails"
        :outlined="filter"
        :dense="filter"
        :background-color="filter ? 'white' : undefined"
        @change="selectionChanged"
        @click:append-outer="openDialog"
        @click:append="clear")
    div(
        v-else-if="!autocomplete && type === 'checkbox'"
        :class="{ 'error--text': hasError }")
        label.dd-group-label(v-if="label") {{ labelText }}
            field-hint-icon(:hint="hint")
        v-row
            v-col(:cols="12" :sm="colSm" class="py-0")
                v-checkbox(
                    v-for="(item, index) in selectItems"
                    :key="item.id"
                    v-if="index <= colBreak"
                    v-model="selectedValue"
                    class="mr-4 mt-3"
                    :value="item.key"
                    :input-value="selectedValue"
                    :name="name"
                    :label="item.label"
                    type="checkbox"
                    color="success"
                    :disabled="isInvalid(item)"
                    hide-details="auto"
                    :error="hasError")
            v-col(:cols="12" :sm="colSm" class="py-0")
                v-checkbox(
                    v-for="(item, index) in selectItems"
                    :key="item.id"
                    v-if="index > colBreak"
                    v-model="selectedValue"
                    class="mr-4 mt-3"
                    :value="item.key"
                    :input-value="selectedValue"
                    :name="name"
                    :label="item.label"
                    type="checkbox"
                    color="success"
                    :disabled="isInvalid(item)"
                    hide-details="auto"
                    :error="hasError")

    //- every form select (single, multiple, searchable): the field shows the value, or removable chips;
    //- the list opens with a search on top for long lists, a check on the picked value, checkboxes
    //- when several can be picked (Klaviyo / Toggl / PandaDoc pattern, tasks#211)
    v-select.dd-select(
        v-else-if="!filter"
        ref="select"
        :value="reverse ? computedValue : selectedValue"
        :label="labelText"
        :placeholder="selectPlaceholder"
        :persistent-placeholder="false"
        :disabled="selectIsDisabled || disabled"
        :items="selectItems"
        item-text="label"
        :item-value="itemValue"
        :name="name"
        :error-messages="errorMessages"
        :multiple="multiSelect"
        :clearable="clearable && !multiSelect && hasValue && !(selectIsDisabled || disabled)"
        clear-icon="close"
        :hide-details="hideDetails"
        :hint="hint"
        :menu-props="ddMenuProps"
        @input="onPick"
        @change="selectionChanged")
        template(v-if="searchable" v-slot:prepend-item)
            .dd-select__search
                v-text-field(
                    ref="search"
                    v-model="search"
                    :placeholder="searchPlaceholder"
                    prepend-inner-icon="search"
                    autocomplete="off"
                    hide-details
                    dense
                    @keydown="onSearchKeydown")
        template(v-slot:selection="{ item }")
            span.dd-select__chip(v-if="multiSelect")
                | {{ item.label }}
                button(
                    v-if="!(selectIsDisabled || disabled)"
                    type="button"
                    :aria-label="`${$t('actions.delete')}: ${item.label}`"
                    @mousedown.stop.prevent
                    @click.stop="removePicked(item[itemValue])")
                    v-icon close
            span.dd-select__value(v-else) {{ item.label }}
        template(v-slot:item="{ item, attrs }")
            .dd-select__option(:class="{ 'dd-select__option--hidden': !matchesSearch(item) }")
                v-icon.dd-select__box(v-if="multiSelect") {{ attrs.inputValue ? 'check_box' : 'check_box_outline_blank' }}
                span.dd-select__label {{ item.label }}
                v-icon.dd-select__check(v-if="!multiSelect && attrs.inputValue") check
        template(v-if="searchable" v-slot:append-item)
            .dd-select__empty(v-if="search && !visibleCount") {{ noResultsText }}

    v-autocomplete(
        v-else-if="reverse"
        v-model="computedValue"
        :label="labelText"
        :disabled="selectIsDisabled || disabled"
        :items="selectItems"
        item-text="label"
        :item-value="itemValue"
        :append-icon="appendIcon"
        :append-outer-icon="appendOuterIcon"
        :name="name"
        :height="innerHeight"
        :error-messages="errorMessages"
        :multiple="multiSelect"
        :small-chips="multiSelect"
        :deletable-chips="multiSelect"
        :hide-details="hideDetails"
        :outlined="filter"
        :dense="filter"
        :background-color="filter ? 'white' : undefined"
        @change="selectionChanged"
        @click:append-outer="openDialog"
        @click:append="clear")

    v-autocomplete(
        v-else
        v-model="selectedValue"
        :label="labelText"
        :disabled="selectIsDisabled || disabled"
        :items="selectItems"
        item-text="label"
        :item-value="itemValue"
        :append-icon="appendIcon"
        :append-outer-icon="appendOuterIcon"
        :name="name"
        :height="innerHeight"
        :error-messages="errorMessages"
        :multiple="multiSelect"
        :small-chips="multiSelect"
        :deletable-chips="multiSelect"
        :hide-details="hideDetails"
        :outlined="filter"
        :dense="filter"
        :background-color="filter ? 'white' : undefined"
        @change="selectionChanged"
        @click:append-outer="openDialog"
        @click:append="clear")
    modal-dialog.entity-select-modal(
        v-model="selectorDialog"
        :title="labelText"
        @ok="acceptSelection"
        scrollable
        cancel-button)
            v-checkbox(
                v-for="value in dialogValues"
                :key="`dialogList-${value}`"
                v-model="dialogSelection"
                class="mr-4 mt-3"
                :value="value"
                :input-value="dialogSelection"
                :name="name"
                :label="getLabel(value)"
                type="checkbox"
                color="success"
                hide-details="auto")
</template>

<script>
import isEqual from 'lodash/isEqual'
import cloneDeep from 'lodash/cloneDeep'
import selectapiType from '@/store/type/selectapiType'
import ErrorMsgInputMixin from './InputMixins/ErrorMsgInputMixin'
import ModalDialog from '@/components/dialog/ModalDialog'
import FieldHintIcon from '@/components/form/FieldHintIcon'

const ENUM_NON_ABOVE = 'non_above'
// lists longer than this get a search on top of the list
const SEARCH_FROM = 8

// case and accent blind: "cafe" finds "Café"
const fold = (text) =>
    String(text ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLocaleLowerCase()

export default {
    components: { ModalDialog, FieldHintIcon },
    mixins: [ErrorMsgInputMixin],
    props: {
        value: {
            type: [Number, String, Object, Array],
            default: null,
        },
        entity: {
            type: String,
            required: true,
        },
        type: {
            type: String,
            default: 'select',
        },
        hideDetails: {
            type: [Boolean, String],
            default: false,
        },
        // the info icon beside the label, like every other input (plugins/fieldHints.js)
        hint: {
            type: String,
            default: undefined,
        },
        clearable: {
            type: Boolean,
            default: true,
        },
        queryParams: {
            type: Object,
            default: null,
        },
        itemsFilter: {
            type: Function,
            default: null,
        },
        errorMessages: {
            type: Array,
            default: () => [],
        },
        invalid: {
            type: Array,
            default: () => [],
        },
        columns: {
            type: Number,
            default: 1,
        },
        height: {
            type: [Number, Boolean],
            default: 32,
        },
        extraOptions: Array,
        disabled: Boolean,
        small: Boolean,
        filter: Boolean,
        hideControl: Boolean,
        requestDisabled: Boolean,
        autocomplete: Boolean,
        multiSelect: Boolean,
        useIdField: Boolean,
        useNameField: Boolean,
        useCodeField: Boolean,
        translateItems: Boolean,
        needsUpdate: Boolean,
        reverse: Boolean,
    },
    data() {
        return {
            selectedValue: undefined,
            dialogValues: undefined,
            dialogSelection: undefined,
            items: [],
            selectIsDisabled: false,
            showClearIcon: false,
            lastQueryParams: null,
            itemsFilled: false,
            appendOuterIcon: undefined,
            search: '',
        }
    },
    computed: {
        // the form select (not the filter bar's, not a checkbox list)
        ddSelect() {
            return (
                !this.filter && (this.autocomplete || this.type !== 'checkbox')
            )
        },
        searchable() {
            return this.selectItems.length >= SEARCH_FROM
        },
        visibleCount() {
            return this.selectItems.filter((item) => this.matchesSearch(item))
                .length
        },
        hasValue() {
            const value = this.reverse ? this.computedValue : this.selectedValue
            return Array.isArray(value)
                ? value.length > 0
                : value !== undefined && value !== null && value !== ''
        },
        ddMenuProps() {
            return {
                offsetY: true,
                maxHeight: 360,
                contentClass: 'dd-select-menu',
            }
        },
        selectPlaceholder() {
            return this.$te('actions.select')
                ? this.$t('actions.select')
                : 'Select'
        },
        noResultsText() {
            return this.$te('texts.select_no_matches')
                ? this.$t('texts.select_no_matches')
                : 'No results'
        },
        searchPlaceholder() {
            return this.$te('labels.search')
                ? this.$t('labels.search')
                : 'Search'
        },
        innerHeight() {
            return this.height && this.height >= 32
                ? this.height +
                      (!this.autocomplete && this.type === 'select' ? 0 : 0)
                : undefined
        },
        colBreak() {
            return Math.floor(this.selectItems.length / 2)
        },
        appendIcon() {
            return this.hideControl
                ? ''
                : this.showClearIcon
                ? 'clear'
                : this.selectedValue
                ? 'expand_more'
                : ''
        },
        selectItems() {
            if (this.translateItems) {
                const items = cloneDeep(this.items)

                items.forEach((item) => {
                    item.label = this.$t(item.label)
                })

                return items
            }

            return this.items
        },

        computedValue: {
            get() {
                if (this.reverse) {
                    return this.selectItems
                        .filter((el) => {
                            return (
                                this.value.findIndex((e) => {
                                    return e == el.key
                                }) === -1
                            )
                        })
                        .map((el) => {
                            return el.key
                        })
                } else {
                    return this.value
                }
            },
            set(val) {
                let value = null
                if (this.reverse) {
                    value = this.selectItems
                        .filter((el) => {
                            return val.indexOf(el.key) === -1
                        })
                        .map((el) => {
                            return el.key
                        })
                } else {
                    value = val
                }
                this.$emit('input', value)
                this.showClearIcon =
                    (this.multiSelect
                        ? value.length > 0
                        : value != undefined) && this.clearable
            },
        },

        colSm() {
            return Math.floor(12 / this.columns)
        },

        hasError() {
            return this.errorMessages.length > 0
        },
        itemValue() {
            return this.useCodeField
                ? 'code'
                : this.useIdField
                ? 'id'
                : this.useNameField
                ? 'name'
                : 'key'
        },
        selectorDialog: {
            get() {
                return !!this.dialogValues
            },
            set() {
                this.dialogValues = undefined
            },
        },
    },
    watch: {
        value: {
            immediate: true,
            handler(value) {
                if (this.multiSelect && value == null) {
                    value = []
                }
                this.selectionChanged()
                if (!isEqual(value, this.selectedValue)) {
                    this.selectedValue = value ? value : undefined
                }
                this.showClearIcon =
                    (this.multiSelect && value instanceof Array
                        ? value.length > 0
                        : value != undefined) && this.clearable
            },
        },
        selectedValue(value) {
            this.$emit('input', value)
            this.showClearIcon =
                (this.multiSelect ? value.length > 0 : value != undefined) &&
                this.clearable
        },
        entity: {
            immediate: true,
            handler() {
                this.updateList()
            },
        },
        queryParams() {
            if (!isEqual(this.queryParams, this.lastQueryParams)) {
                this.updateList()
            }
        },
        needsUpdate: 'updateList',
    },
    mounted() {
        this.selectionChanged()
        if (this.ddSelect) {
            // a fresh search each time the list opens; the whole list is rendered while searching
            this.$watch(
                () => this.$refs.select?.isMenuActive,
                (open) => {
                    if (!open) {
                        this.search = ''
                        return
                    }
                    // the first opening builds the list first, so try again a bit later
                    const focusSearch = () =>
                        this.$refs.search?.$refs.input?.focus()
                    setTimeout(focusSearch, 150)
                    setTimeout(focusSearch, 400)
                }
            )
            this.$watch('search', (text) => {
                if (text && this.$refs.select)
                    this.$refs.select.lastItem = this.selectItems.length
            })
        }
        if (this.reverse && !this.multiSelect) {
            // eslint-disable-next-line no-console
            console.error('Reverse selection only works with multi select.')
        }
    },
    methods: {
        clear() {
            if (
                this.multiSelect
                    ? this.selectedValue.length
                    : this.selectedValue
            ) {
                this.selectedValue = this.multiSelect ? [] : undefined
            }
        },
        onPick(value) {
            if (this.reverse) this.computedValue = value
            else this.selectedValue = value
        },
        removePicked(value) {
            const current = this.reverse
                ? this.computedValue
                : this.selectedValue
            this.onPick(current.filter((v) => v !== value))
        },
        matchesSearch(item) {
            return !this.search || fold(item.label).includes(fold(this.search))
        },
        // typing stays in the search; arrows, Enter, Esc and Tab still drive the list
        onSearchKeydown(event) {
            if (
                !['ArrowDown', 'ArrowUp', 'Enter', 'Escape', 'Tab'].includes(
                    event.key
                )
            )
                event.stopPropagation()
        },
        getLabel(value) {
            return this.selectItems.find(
                (item) => item[this.itemValue] === value
            )?.label
        },
        acceptSelection() {
            this.selectedValue = this.reverse
                ? [
                      ...[],
                      ...this.selectedValue,
                      ...this.dialogValues.filter(
                          (value) =>
                              this.dialogSelection.findIndex(
                                  (selected) => selected === value
                              ) === -1
                      ),
                  ]
                : cloneDeep(this.dialogSelection)
            this.dialogSelection = undefined
            this.dialogValues = undefined
        },
        openDialog() {
            this.dialogSelection = this.reverse
                ? cloneDeep(this.computedValue)
                : cloneDeep(this.selectedValue)
            this.dialogValues = this.reverse
                ? cloneDeep(this.computedValue)
                : cloneDeep(this.selectedValue)
        },
        selectionChanged() {
            if (this.multiSelect && this.selectedValue) {
                const isTheLastSelectedEqualsNoneAbove =
                    this.selectedValue[this.selectedValue.length - 1] ===
                    ENUM_NON_ABOVE
                const hasNonAbove = this.selectedValue.includes(ENUM_NON_ABOVE)

                if (this.selectedValue.length > 1) {
                    if (isTheLastSelectedEqualsNoneAbove) {
                        this.selectedValue.splice(
                            0,
                            this.selectedValue.length - 1
                        )
                    } else if (hasNonAbove) {
                        const index = this.selectedValue.indexOf(ENUM_NON_ABOVE)
                        if (index !== -1) {
                            this.selectedValue.splice(index, 1)
                        }
                    }
                }
            }

            if (
                this.innerHeight &&
                !this.ddSelect &&
                !(!this.autocomplete && this.type === 'checkbox')
            ) {
                let selector = this.$refs[`entitySelect-${this._uid}`]
                if (selector) {
                    this.$nextTick(() => {
                        let selection = selector.querySelector(
                            '.v-select__selections'
                        )
                        if (selection) {
                            this.appendOuterIcon =
                                selection.scrollHeight > this.innerHeight
                                    ? 'more_horiz'
                                    : undefined
                        }
                    })
                }
            }
        },
        updateList() {
            if (this.requestDisabled) {
                return
            }

            this.selectIsDisabled = true
            this.lastQueryParams = this.queryParams

            this.$store
                .dispatch(
                    selectapiType.getActionName(
                        this.entity,
                        selectapiType.actions.LOAD
                    ),
                    { query: this.queryParams }
                )
                .then((data) => {
                    this.selectIsDisabled = false
                    this.items = this.itemsFilter
                        ? this.itemsFilter(data)
                        : data
                    this.extraOptions && this.extraOptions.length
                        ? (this.items = [...this.extraOptions, ...this.items])
                        : this.items
                    this.$emit('getItems', this.items)
                    this.itemsFilled = true
                    this.validateSelectedValue()
                })
        },
        validateSelectedValue() {
            // a disabled field cannot be edited, so it keeps its stored value even when the
            // list no longer offers it (e.g. a past group order's former partner restaurant);
            // clearing it silently changed the form and lost data on the next save
            if (
                this.disabled ||
                !this.itemsFilled ||
                this.selectedValue === undefined ||
                (this.multiSelect && this.selectedValue.length === 0)
            ) {
                return
            }

            if (this.multiSelect) {
                const validList = []

                this.selectedValue.forEach((selected) => {
                    const found = this.items.find(
                        (item) => item[this.itemValue] === selected
                    )

                    if (found !== undefined) {
                        validList.push(selected)
                    }
                })

                this.selectedValue = validList
            } else {
                const found = this.items.find(
                    (item) => item[this.itemValue] === this.selectedValue
                )

                if (found === undefined) {
                    this.clear()
                }
            }
        },
        isInvalid(item) {
            if (this.invalid?.length) {
                const idx = this.invalid.indexOf(item.key)
                return idx !== -1 ? true : false
            }
            return false
        },
    },
}
</script>
<style lang="scss">
.entity-selector {
    &.entity-select-dialog {
        .v-select__selections {
            padding-right: 36px;
        }
    }
    .v-input:not(.v-text-field--outlined),
    .v-input.v-select--is-multi {
        .v-input__slot {
            .v-select__slot {
                height: inherit;
                .v-select__selections {
                    height: inherit;
                    overflow: hidden;
                }
            }
        }
        .v-input__append-outer {
            position: absolute;
            right: 44px;
        }
    }
    .v-input.v-select--is-multi.v-text-field--outlined {
        .v-select__slot {
            height: 40px;
        }
    }
    .v-select__selections {
        .v-chip.v-size--default {
            height: 30px;
        }
        .v-chip.v-size--small {
            margin-top: 5px;
            margin-bottom: 5px;
        }
    }
    .v-input__slot {
        transition: margin-bottom var(--transition-speed);
    }
    .v-text-field__details,
    .v-messages {
        transition: min-height var(--transition-speed);
    }
    &.entity-selector-small {
        .v-select.v-text-field input {
            padding: 0;
            max-height: 20px;
            font-size: 14px;
        }
    }
    .v-text-field > .v-input__control > .v-input__slot:before {
        z-index: 2;
    }
}

// ---- form select (tasks#211) ----------------------------------------------------------------
// picked values: #f1f1f1 chips with their own remove button, wrapping onto more lines
.entity-selector .dd-select {
    .v-select__selections {
        flex-wrap: nowrap;
        min-width: 0;
    }

    &.v-select--is-multi .v-select__selections {
        flex-wrap: wrap;
        gap: 6px;
        padding: 6px 0;
    }

    // typing goes to the list's search, the field's own input only keeps the focus
    .v-select__selections input {
        width: 0;
        min-width: 0;
        padding: 0;
    }

    // the clear button of a single value: small and grey next to the chevron
    .v-input__icon--clear .v-icon,
    .v-input__icon--clear .v-icon svg {
        width: 16px !important;
        height: 16px !important;
        color: #767676 !important;
        font-size: 16px !important;
    }

    .dd-select__value {
        overflow: hidden;
        color: #191919;
        font-size: 16px;
        font-weight: 500;
        line-height: 20px;
        white-space: nowrap;
        text-overflow: ellipsis;
    }
}

.dd-select__chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    max-width: 100%;
    height: 28px;
    padding: 0 4px 0 10px;
    border-radius: 14px;
    background: #e7e7e7;
    color: #191919;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;

    button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 20px;
        height: 20px;
        border-radius: 10px;

        &:hover {
            background: rgba(25, 25, 25, 0.1);
        }

        .v-icon {
            width: 14px;
            height: 14px;
            color: #191919 !important;
            font-size: 14px !important;
        }

        .v-icon svg {
            width: 14px !important;
            height: 14px !important;
        }

        &:focus-visible {
            outline: 2px solid rgba(25, 25, 25, 0.66);
        }
    }
}

// the list: search on top (sticky), 40px rows, a check or a checkbox, no Vuetify ripple tint
.v-menu__content.dd-select-menu {
    .v-list {
        padding: 0 0 8px;
    }

    .dd-select__search {
        position: sticky;
        top: 0;
        z-index: 1;
        padding: 8px;
        background: #fff;
        border-bottom: 1px solid #e7e7e7;

        .v-input__slot {
            min-height: 36px !important;
            border-radius: 18px !important;
        }
    }

    .v-list-item {
        min-height: 40px;
    }

    .v-list-item:has(> .dd-select__option--hidden) {
        display: none;
    }

    .v-list-item--active::before {
        opacity: 0 !important;
    }

    .v-list-item--active {
        background: #f7f7f7;
    }

    .dd-select__option {
        display: flex;
        align-items: center;
        gap: 12px;
        width: 100%;
        color: #191919;
        font-size: 15px;
        line-height: 20px;
    }

    .dd-select__label {
        flex: 1 1 auto;
        min-width: 0;
    }

    .v-list-item--active .dd-select__label {
        font-weight: 600;
    }

    .dd-select__box,
    .dd-select__check {
        flex: none;
        color: #191919 !important;
    }

    .dd-select__empty {
        padding: 12px 16px 4px;
        color: #606060;
        font-size: 14px;
    }
}
</style>
